import React, { useState, useEffect } from 'react';
import { Package, Search, Building, User, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { fetchHospedajes } from '@/services/hospedajes.service';
import { createInventarioLenceria } from '@/pages/inventario/lenceria/services/inventarioLenceria.services';

const AddToInventoryModal = ({ 
  isOpen, 
  onClose, 
  garmentType, 
  onSuccess 
}) => {
  const [hospedajes, setHospedajes] = useState([]);
  const [selectedHospedaje, setSelectedHospedaje] = useState(null);
  const [cantidad, setCantidad] = useState(1);
  const [descripcion, setDescripcion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen && garmentType) {
      loadHospedajes();
      setDescripcion(`${garmentType.nombre} - Agregado desde tipos de prenda`);
    }
  }, [isOpen, garmentType]);

  const loadHospedajes = async () => {
    setIsLoading(true);
    try {
      const response = await fetchHospedajes({ 
        limit: 100, 
        offset: 1, 
        query: { estado: 'Activo' } 
      });
      setHospedajes(response.data || []);
    } catch (error) {
      console.error('Error cargando hospedajes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredHospedajes = hospedajes.filter(hospedaje =>
    hospedaje.nombrePropiedad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    hospedaje.edificio?.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async () => {
    if (!selectedHospedaje || cantidad <= 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const inventarioData = {
        nombre: garmentType.nombre,
        peso: garmentType.peso / 1000, // Convertir de gramos a kilogramos
        cantidad: parseInt(cantidad),
        descripcion: descripcion,
        estado: 'Activo',
        propiedad: {
          id: selectedHospedaje._id,
          nombre: selectedHospedaje.nombrePropiedad,
          direccion: selectedHospedaje.direccion || 'Sin dirección',
          coordinador: {
            id: selectedHospedaje.coordinador.id,
            nombre: selectedHospedaje.coordinador.nombre
          },
          edificio: {
            id: selectedHospedaje.edificio.id,
            nombre: selectedHospedaje.edificio.nombre,
            ciudad: selectedHospedaje.edificio.ciudad
          }
        }
      };

      await createInventarioLenceria(inventarioData);
      onSuccess?.(`${garmentType.nombre} agregado al inventario de ${selectedHospedaje.nombrePropiedad}`);
      handleClose();
    } catch (error) {
      console.error('Error agregando al inventario:', error);
      onSuccess?.(`Error al agregar ${garmentType.nombre} al inventario`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedHospedaje(null);
    setCantidad(1);
    setDescripcion('');
    setSearchTerm('');
    onClose();
  };

  if (!garmentType) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Package className="h-5 w-5" />
            <span>Agregar al Inventario de Lencería</span>
          </DialogTitle>
          <DialogDescription>
            Selecciona el hospedaje donde agregar "{garmentType.nombre}" al inventario
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Información del tipo de prenda */}
          <Card>
            <CardContent className="p-4">
              <h4 className="font-medium mb-2">Tipo de Prenda Seleccionado</h4>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold">{garmentType.nombre}</p>
                  <p className="text-sm text-muted-foreground">
                    Peso: {garmentType.peso}g ({(garmentType.peso / 1000).toFixed(2)}kg)
                  </p>
                </div>
                <Badge variant="outline">
                  {garmentType.estado}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Búsqueda de hospedajes */}
          <div className="space-y-2">
            <Label>Buscar Activo</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Buscar por nombre de propiedad o edificio..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Lista de hospedajes */}
          <div className="space-y-2">
            <Label>Seleccionar Hospedaje *</Label>
            <div className="max-h-60 overflow-y-auto border rounded-md">
              {isLoading ? (
                <div className="p-4 text-center text-muted-foreground">
                  Cargando hospedajes...
                </div>
              ) : filteredHospedajes.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No se encontraron hospedajes
                </div>
              ) : (
                filteredHospedajes.map((hospedaje) => (
                  <div
                    key={hospedaje._id}
                    className={`p-3 border-b cursor-pointer hover:bg-muted/50 transition-colors ${
                      selectedHospedaje?._id === hospedaje._id ? 'bg-primary/10 border-primary' : ''
                    }`}
                    onClick={() => setSelectedHospedaje(hospedaje)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{hospedaje.nombrePropiedad}</span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                          <div className="flex items-center space-x-1">
                            <Building className="h-3 w-3" />
                            <span>{hospedaje.edificio?.nombre}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <MapPin className="h-3 w-3" />
                            <span>{hospedaje.edificio?.ciudad}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <User className="h-3 w-3" />
                            <span>{hospedaje.coordinador?.nombre}</span>
                          </div>
                        </div>
                      </div>
                      {selectedHospedaje?._id === hospedaje._id && (
                        <div className="w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Cantidad */}
          <div className="space-y-2">
            <Label htmlFor="cantidad">Cantidad *</Label>
            <Input
              id="cantidad"
              type="number"
              min="1"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
              placeholder="Cantidad a agregar"
            />
          </div>

          {/* Descripción */}
          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción</Label>
            <Input
              id="descripcion"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Descripción adicional..."
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={!selectedHospedaje || cantidad <= 0 || isSubmitting}
            >
              {isSubmitting ? 'Agregando...' : 'Agregar al Inventario'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddToInventoryModal;
